/**
 * Hierarchical query key factory for the Cases feature.
 *
 * @module features/cases/queryKeys
 *
 * @remarks
 * Using a factory instead of ad-hoc string arrays ensures that:
 * - Cache invalidations are precise (`queryClient.invalidateQueries({ queryKey: caseKeys.all() })`)
 * - Keys are type-safe and refactor-friendly
 * - The key structure is documented in one place
 *
 * ## Key hierarchy
 * ```
 * ["cases"]                          ← invalidates everything case-related
 * ["cases", "list", CaseFilters]     ← invalidates filtered list views
 * ["cases", "detail", id]            ← invalidates a single case
 * ```
 *
 * @example
 * ```typescript
 * // Invalidate all cases after a mutation
 * queryClient.invalidateQueries({ queryKey: caseKeys.all() });
 *
 * // Invalidate only the detail for case-001
 * queryClient.invalidateQueries({ queryKey: caseKeys.detail("case-001") });
 * ```
 */

import type { CaseFilters } from "./types";

export const caseKeys = {
  /** Root key — use to invalidate all case-related cache entries. */
  all: () => ["cases"] as const,

  /** Key for a filtered list of cases. */
  list: (filters: CaseFilters) => ["cases", "list", filters] as const,

  /** Key for a single case detail. */
  detail: (id: string) => ["cases", "detail", id] as const,
};
